import { FunctionHookRegistry, HookType } from './FunctionHookRegistry.js';
export { HookType };

/**
 * Centralized function hooking system allowing multiple independent actors to hook functions on the same context
 * object without conflicts. The main design goals are:
 * - WeakMap registry to prevent memory leaks,
 * - owner-based isolation to prevent conflicts,
 * - single wrapper per function with FIFO execution, though, actors are not aware of one another, and
 * - intruder-based masking (enable/disable) of hooks without removing them.
 *
 * @example
 * const intrusion = new FunctionIntrusion(viewer.impl);
 * intrusion.addHook('tick', (context, args) => { ... }, HookType.Before);
 * intrusion.addHook('tick', (context, args, result) => { ... }, HookType.After);
 * ...
 * intrusion.removeAllHooks();
 */
export class FunctionIntrusion {

  static #registriesByFunction = new WeakMap();
  static #intrusionCounter = 0;

  #targetContext;
  #owner; // Unique symbol for this intrusion instance

  /**
   * Creates a FunctionIntrusion for the specified context object.
   * @param {Object} targetContext - The context object containing functions to intrude
   */
  constructor(targetContext) {
    if (!targetContext || typeof targetContext !== 'object') {
      throw new Error('Target context is required');
    }
    this.#targetContext = targetContext;
    this.#owner = Symbol(`${this.constructor.name}-${++FunctionIntrusion.#intrusionCounter}`);
  }

  /**
   * Ensures a hook registry exists for the target function.
   * @private
   * @param {string} functionName - The name of the function to ensure registry for
   * @returns {FunctionHookRegistry|undefined} The hook registry for the function or undefined if function doesn't exist
   */
  #getOrCreateRegistry(functionName) {
    const currentFunction = this.#targetContext[functionName];
    if (!(currentFunction instanceof Function)) {
      console.warn(`FunctionIntrusion: ${functionName} is not a function on the target context`);
      return undefined;
    }

    let registry = FunctionIntrusion.#registriesByFunction.get(currentFunction);
    if (registry) return registry;

    registry = new FunctionHookRegistry(currentFunction);
    const wrapper = registry.createWrapper();

    FunctionIntrusion.#registriesByFunction.set(wrapper, registry);
    this.#targetContext[functionName] = wrapper;
    return registry;
  }

  /**
   * Internal method to enable or disable hooks for a specific function or all functions.
   * @private
   * @param {string|undefined} [functionName] - The name of the function to enable/disable hooks for. If undefined, affects all functions.
   * @param {boolean} enabled - Whether to enable or disable the hooks
   */
  #setHooksEnabled(functionName, enabled) {
    const descriptors = functionName === undefined ?
    Object.values(Object.getOwnPropertyDescriptors(this.#targetContext)) :
    [{ value: this.#targetContext[functionName] }];

    const functions = descriptors.map((descriptor) => descriptor.value).filter((f) => f instanceof Function);
    for (const f of functions) {
      const registry = FunctionIntrusion.#registriesByFunction.get(f);
      enabled ? registry?.enable(this.#owner) : registry?.disable(this.#owner);
    }
  }

  /**
   * Hooks a callback to the specified function. The callback is called before, after, or instead of the original
   * function based on the hook type @see HookType.
   * @param {string} functionName - The name of the function to hook
   * @param {Function} callback - Function to call as the hook
   * @param {HookType} [hookType] - The type of hook, defaults to Before
   */
  addHook(functionName, callback, hookType = HookType.Before) {
    if (typeof functionName !== 'string') {
      console.warn('FunctionIntrusion: Function name must be a string');
      return;
    }
    if (!(callback instanceof Function)) {
      console.warn('FunctionIntrusion: Callback must be a function');
      return;
    }
    const registry = this.#getOrCreateRegistry(functionName);
    registry?.addHook(hookType, callback, this.#owner);
  }

  /**
   * Removes a specific hook from a function.
   * @param {string} functionName - The name of the function
   * @param {Function} callback - The callback function to remove
   * @param {HookType} [hookType] - The hook type, defaults to Before
   */
  removeHook(functionName, callback, hookType = HookType.Before) {
    const currentFunction = this.#targetContext[functionName];
    if (!currentFunction) {
      console.warn(`FunctionIntrusion: Function ${functionName} not found on target context`);
      return;
    }

    const registry = FunctionIntrusion.#registriesByFunction.get(currentFunction);
    if (!registry) return;

    registry.removeHook(hookType, callback, this.#owner);
    this.#removeRegistryIfEmpty(functionName, registry);
  }

  /**
   * Removes all hooks registered by this intrusion instance. This does not affect hooks registered by other
   * intrusion instances.
   */
  removeAllHooks() {
    const descriptors = Object.getOwnPropertyDescriptors(this.#targetContext);
    for (const [functionName, descriptor] of Object.entries(descriptors)) {
      if (!(descriptor.value instanceof Function)) continue;

      const registry = FunctionIntrusion.#registriesByFunction.get(descriptor.value);
      if (!registry) continue;

      registry.removeHooksByOwner(this.#owner);
      this.#removeRegistryIfEmpty(functionName, registry);
    }
  }

  #removeRegistryIfEmpty(functionName, registry) {
    if (registry.hasHooks()) return;

    const originalFunction = registry.getOriginalFunction();
    this.#targetContext[functionName] = originalFunction;
    FunctionIntrusion.#registriesByFunction.delete(this.#targetContext[functionName]);
  }

  getOriginalFunction(functionName) {
    const currentFunction = this.#targetContext[functionName];
    if (!currentFunction) return undefined;

    const registry = FunctionIntrusion.#registriesByFunction.get(currentFunction);
    return registry?.getOriginalFunction() || currentFunction;
  }

  /**
   * Enables hooks for a specific function or all functions. The hooks are only unmasked for this intrusion instance.
   * @param {string} [functionName] - The name of the function to enable hooks for. If undefined, enables all functions.
   */
  enableHooks(functionName) {
    this.#setHooksEnabled(functionName, true);
  }

  /**
   * Disables hooks for a specific function or all functions without removing them. The hooks are only masked for
   * this intrusion instance, allowing other instances to still use them. In case of replace hooks, the original
   * function will be called if all hooks of all intruders on the function are masked.
   * @param {string} [functionName] - The name of the function to disable hooks for. If undefined, disables all functions.
   */
  disableHooks(functionName) {
    this.#setHooksEnabled(functionName, false);
  }

  areHooksEnabled(functionName) {
    const currentFunction = this.#targetContext[functionName];
    if (!currentFunction) return false;

    const registry = FunctionIntrusion.#registriesByFunction.get(currentFunction);
    return registry?.isEnabled(this.#owner) || false;
  }
}